<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
    @click="handleClickOutside">
    <div
      class="relative w-full max-w-4xl p-4 sm:p-6 md:p-8 mx-auto bg-white shadow-2xl rounded-xl max-h-[90vh] overflow-y-auto"
      @click.stop>
      <div class="flex items-center justify-between mb-4 sm:mb-6">
        <h3 class="text-lg font-semibold text-gray-900 sm:text-xl">
          Detail Dokumen
        </h3>
        <button type="button" aria-label="Close" @click="closeModal"
          class="text-gray-400 transition hover:text-gray-700">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div>
        <slot>
          <div class="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
            <!-- Left Column: Document Info -->
            <div class="space-y-6">
              <!-- Document Icon and Basic Info -->
              <div class="flex flex-row items-start gap-4">
                <div class="flex flex-col justify-center flex-1 space-y-3">
                  <p class="text-gray-600">
                    <strong>Nama Dokumen:</strong><br />
                    <span class="text-lg font-medium text-gray-900">{{
                      dokumentdata.nama_dok || "-"
                      }}</span>
                  </p>
                  <p class="text-gray-600">
                    <strong>Jenis Dokumen:</strong><br />{{
                      dokumentdata.jenisdokumen?.nama || "-"
                    }}
                  </p>
                  <p class="text-gray-600">
                    <strong>Kategori:</strong><br />{{
                      dokumentdata.publish || "-"
                    }}
                  </p>
                </div>
              </div>

              <!-- Description -->
              <div>
                <strong class="text-gray-900">Deskripsi:</strong>
                <div v-if="dokumentdata.deskripsi" class="p-4 mt-2 prose-sm prose rounded-lg bg-gray-50 max-w-none"
                  v-html="dokumentdata.deskripsi"></div>
                <p v-else class="mt-2 italic text-gray-500">
                  Tidak ada deskripsi
                </p>
              </div>


            </div>

            <!-- Right Column: Files Preview -->
            <div v-if="dokumentdata.file_path && dokumentdata.file_path.length" class="space-y-4">
              <strong class="text-gray-900">File Dokumen:</strong>
              <div class="space-y-3">
                <div v-for="(file, index) in dokumentdata.file_path" :key="index" class="p-3 border rounded-lg sm:p-4">
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-sm font-medium text-gray-700">File {{ index + 1 }}</span>
                    <a :href="file" target="_blank"
                      class="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 rounded-full bg-blue-50 hover:bg-blue-100">
                      <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Buka
                    </a>
                  </div>

                  <!-- File Preview -->
                  <div v-if="isImage(file)" class="w-full">
                    <img :src="file" alt="Document Preview"
                      class="object-contain w-full border rounded max-h-48 sm:max-h-64" />
                  </div>
                  <div v-else-if="isPdf(file)" class="w-full">
                    <embed :src="file" type="application/pdf" class="w-full h-48 border rounded sm:h-64" />
                  </div>
                  <div v-else class="flex items-center justify-center w-full h-32 bg-gray-100 border rounded">
                    <div class="text-center">
                      <svg class="w-8 h-8 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p class="mt-1 text-sm text-gray-500">
                        File tidak dapat dipratinjau
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Detail Dokumen",
  },
  dokumendata: {
    type: [Array, Object],
    required: true,
  },
});

const emit = defineEmits(["update:isOpen"]);

// Close modal function
const closeModal = () => {
  emit("update:isOpen", false);
};

// Handle clicking outside the modal to close it
const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

// Get document data
const dokumentdata = computed(() => {
  return Array.isArray(props.dokumendata)
    ? props.dokumendata[0] || {}
    : props.dokumendata || {};
});

// Check if file is an image
function isImage(filePath) {
  return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filePath);
}

// Check if file is a PDF
function isPdf(filePath) {
  return /\.pdf$/i.test(filePath);
}

// Format date
function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
</script>

<style scoped>
.prose ul {
  list-style: disc;
  padding-left: 1.25rem;
}

.prose ol {
  list-style: decimal;
  padding-left: 1.25rem;
}

.prose p {
  margin-bottom: 1rem;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  margin-bottom: 0.5rem;
  margin-top: 1rem;
  font-weight: 600;
}
</style>
